import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createTargetingClient } from '../api';
import { createTargetingBuilderClient } from './api';
import type { TargetingBuilderFilter } from './model';

export const targetingBuilderQueryKey = ['targeting-builder'] as const;
export const targetingBuilderListsQueryKey = [...targetingBuilderQueryKey, 'list'] as const;

export function useTargetingBuilderClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createTargetingBuilderClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useStandaloneTargetingClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createTargetingClient({
    config: { apiBaseUrl: config.madBuyServiceUrl, networkId: config.networkId },
    rootApiConfig: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useTargetingBuildersQuery(filter: TargetingBuilderFilter) {
  const client = useTargetingBuilderClient();
  return useQuery({
    queryKey: [...targetingBuilderListsQueryKey, filter],
    queryFn: () => client.list(filter),
  });
}

export function useTargetingBuilderQuery(id: string | undefined) {
  const client = useTargetingBuilderClient();
  return useQuery({
    queryKey: [...targetingBuilderQueryKey, 'detail', id],
    enabled: Boolean(id),
    queryFn: () => client.get(id ?? ''),
    refetchOnMount: 'always',
  });
}

export function useTargetingBuilderDeviceTypesQuery() {
  const client = useTargetingBuilderClient();
  return useQuery({
    queryKey: [...targetingBuilderQueryKey, 'device-types'],
    queryFn: client.listDeviceTypes,
  });
}

export function useTargetingBuilderMutations() {
  const client = useTargetingBuilderClient();
  const queryClient = useQueryClient();
  const invalidateLists = () =>
    queryClient.invalidateQueries({ queryKey: targetingBuilderListsQueryKey });
  return {
    create: useMutation({ mutationFn: client.create, onSuccess: invalidateLists }),
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: unknown }) => client.update(id, payload),
      onSuccess: invalidateLists,
    }),
  };
}

export function useTargetingStatusCountsQuery(searchTerm: string) {
  const client = useTargetingBuilderClient();
  const search = searchTerm.trim();
  return useQuery({
    queryKey: [...targetingBuilderListsQueryKey, { statusCounts: true }, search],
    queryFn: () => client.getStatusCounts(search),
  });
}
